import React, { useContext, useMemo, useState } from 'react';
import PrivilegesContext from '@ubeya/shared-web/contexts/PrivilegesContext';
import privileges from '@ubeya/shared-web/privileges';
import { ModuleIcons, SubmoduleIcons, LogoIcons } from '@ubeya/shared-web/assets/SidebarIcons';
import { AppSideBar } from '@ubeya/shared-web/components';
import useAccountAdminconfig from '@ubeya/shared/hooks/account/useAccountAdminConfig';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useModules from '@ubeya/shared/hooks/account/useModules';
import useUser from '@ubeya/shared/hooks/auth/useUser';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useGetHolidayAccessories from '@ubeya/shared-web/hooks/useGetHolidayAccessories';
import styled, { useTheme } from 'styled-components';
import { ReactComponent as TagBeta } from '@ubeya/shared-web/assets/tag-beta.svg';
import { ACCOUNT_SETTINGS_WITHOUT_EDIT_PERMISSIONS_ORGANIZATION_IDS } from '@ubeya/shared/utils/accounts';
import { AccountLink } from 'shared/components';
import ProfileModal from 'shared/components/ProfileMenu/ProfileModal';
import { MobileTopBar } from '../../shared/components/AccountHeader/MobileTopBar';
import { isAdminBlockedFromSettings } from '../../settings/utils';
import { getCanAccessAiAgent } from '../../aiAgent/aiAgentAccess';
const StyledTagBeta = styled(TagBeta)`
  position: absolute;
  top: -4px;
  right: -2px;
  width: 22px;
  height: auto;
`;

const BetaAccessory = () => <StyledTagBeta />;

const SideBar = () => {
  const [profileModal, setProfileModal] = useState(false);
  const myPrivileges = useContext(PrivilegesContext);
  const { isAccountNetworkAccessSupplier, enablePayrollProcessFeature, billingStatus } = useContext(ConfigContext);
  const { accountId, account } = useAccount();
  const {
    data: { id: adminId, email: adminEmail },
    isUbeyaAdmin,
    accounts
  } = useUser();

  const isHost = account.isHost;

  const { isRestrictedFromEmployees } = accounts?.find(({ id }) => id === accountId) || {};

  const { LogoAccessory } = useGetHolidayAccessories();

  const { adminConfigData, updateAdminConfig } = useAccountAdminconfig({ accountId, adminId, isUbeyaAdmin });
  const { modules } = useModules();
  const theme = useTheme();

  const canAccessAiAgent = getCanAccessAiAgent({
    isUbeyaAdmin,
    adminId,
    accountId,
    adminPrivileges: myPrivileges,
    billingStatus
  });

  const links = useMemo(
    () => [
      [
        {
          label: '',
          mobileLabel: 'dashboard',
          to: '/:accountId/dashboard',
          order: 0,
          icon: LogoIcons.LogoDefault,
          iconDark: () => <LogoIcons.LogoDefault fill={theme.colors.black} />,
          active: () => <LogoIcons.LogoDefault fill={theme.colors.primary} />,
          mainIcon: true,
          accessory: LogoAccessory
        },
        {
          label: 'people',
          to: '/:accountId/employees',
          disabled: isHost,
          order: isHost ? 4 : 1,
          icon: ModuleIcons.ModuleIconsLight.Workers,
          iconDark: ModuleIcons.ModuleIconsDark.WorkersDark,
          active: ModuleIcons.ModuleIconsActive.WorkersActive,
          isVisible:
            (!!myPrivileges[privileges.BRANCH_ADMIN] || !!myPrivileges[privileges.ACCOUNT_ADMIN]) &&
            !isRestrictedFromEmployees,
          secondaryMenu: [
            {
              label: 'employees',
              to: '/:accountId/employees',
              icon: SubmoduleIcons.Workers
            },
            {
              label: 'compliance',
              to: '/:accountId/compliances',
              icon: SubmoduleIcons.Compliance,
              isHidden: !myPrivileges[privileges.COMPLIANCE] && !isAccountNetworkAccessSupplier
            },
            {
              label: 'hiring',
              to: '/:accountId/hiring',
              icon: SubmoduleIcons.Workers,
              isHidden: !myPrivileges[privileges.ATS] || !modules?.ats
            }
          ]
        },
        {
          label: 'timesheet',
          to: '/:accountId/timesheet',
          includePath: ['live'],
          order: 2,
          disabled: false,
          icon: ModuleIcons.ModuleIconsLight.Timesheet,
          iconDark: ModuleIcons.ModuleIconsDark.TimesheetDark,
          active: ModuleIcons.ModuleIconsActive.TimesheetActive,
          isVisible: !!myPrivileges[privileges.TIMESHEET],
          secondaryMenu: [
            // {
            //   label: 'timesheet',
            //   to: '/:accountId/timesheet',
            //   icon: SubmoduleIcons.Timesheet
            // }
            // {
            //   label: 'externalTimesheets',
            //   to: '/:accountId/external-timesheet',
            //   icon: SubmoduleIcons.ExternalTimesheet
            // }
          ]
        },
        {
          label: 'scheduling',
          to: '/:accountId/scheduling',
          order: isHost ? 1 : 3,
          icon: ModuleIcons.ModuleIconsLight.Calendar,
          iconDark: ModuleIcons.ModuleIconsDark.CalendarDark,
          active: ModuleIcons.ModuleIconsActive.CalendarActive,
          isVisible: !!myPrivileges[privileges.SCHEDULING]
        },
        {
          label: 'finance',
          to: '/:accountId/payroll',
          order: isHost ? 6 : 4,
          disabled: isHost,
          icon: ModuleIcons.ModuleIconsLight.Finance,
          iconDark: ModuleIcons.ModuleIconsDark.FinanceDark,
          active: ModuleIcons.ModuleIconsActive.FinanceActive,
          isVisible: !!myPrivileges[privileges.PAYROLL],
          secondaryMenu: [
            {
              label: 'payroll',
              to: '/:accountId/payroll',
              icon: SubmoduleIcons.Payroll
            },
            {
              label: 'invoices',
              to: '/:accountId/invoices',
              icon: SubmoduleIcons.Invoices,
              isHidden: !myPrivileges[privileges.INVOICE]
            },
            {
              label: 'payrollProcessHistory',
              to: '/:accountId/process-payroll-history',
              icon: SubmoduleIcons.PayrollHistory,
              isHidden:
                !enablePayrollProcessFeature ||
                !myPrivileges[privileges.EXPORT_PROCESS_PAYROLL] ||
                !myPrivileges[privileges.ACCOUNT_ADMIN]
            }
            // {
            //   label: 'statements',
            //   to: '/:accountId/statements',
            //   icon: SubmoduleIcons.Statements
            // },
            // {
            //   label: 'payments',
            //   to: '/:accountId/payments',
            //   icon: SubmoduleIcons.Payments
            // }
          ]
        },
        {
          label: 'communication',
          to: '/:accountId/feed',
          order: isHost ? 7 : 5,
          disabled: isHost,
          icon: ModuleIcons.ModuleIconsLight.Comm,
          iconDark: ModuleIcons.ModuleIconsDark.CommDark,
          active: ModuleIcons.ModuleIconsActive.CommActive,
          isVisible:
            !!myPrivileges[privileges.FEED] ||
            !!myPrivileges[privileges.SEQUENCE] ||
            !!myPrivileges[privileges.SHIFT_TASKS] ||
            (!!modules?.help_desk &&
              (!!myPrivileges[privileges.BRANCH_ADMIN] || !!myPrivileges[privileges.ACCOUNT_ADMIN])),
          secondaryMenu: [
            {
              label: 'feed',
              to: '/:accountId/feed',
              icon: SubmoduleIcons.Feed,
              isHidden: !myPrivileges[privileges.FEED]
            },
            {
              label: 'sequences',
              to: '/:accountId/sequences',
              icon: SubmoduleIcons.Workflows,
              isHidden: !myPrivileges[privileges.SEQUENCE]
            },
            {
              label: 'timesheet_sequences',
              to: '/:accountId/timesheet-sequences',
              icon: SubmoduleIcons.TimesheetWorkflows,
              isHidden: !myPrivileges[privileges.SHIFT_TASKS]
            },
            {
              label: 'help_desk',
              to: '/:accountId/help-desk',
              icon: SubmoduleIcons.HelpDesk,
              isHidden:
                !modules?.help_desk ||
                (!myPrivileges[privileges.BRANCH_ADMIN] && !myPrivileges[privileges.ACCOUNT_ADMIN])
            }
          ]
        },
        {
          label: 'reports',
          to: '/:accountId/reports',
          order: isHost ? 8 : 6,
          disabled: isHost,
          icon: ModuleIcons.ModuleIconsLight.Reports,
          iconDark: ModuleIcons.ModuleIconsDark.ReportsDark,
          active: ModuleIcons.ModuleIconsActive.ReportsActive,
          isVisible: !!myPrivileges[privileges.REPORTS],
          secondaryMenu: [
            {
              label: 'reports',
              to: '/:accountId/reports',
              icon: SubmoduleIcons.Reports
            },
            ...(account?.enableBi && (isUbeyaAdmin || (myPrivileges[privileges.PAYROLL] && privileges.ACCOUNT_ADMIN))
              ? [
                  {
                    label: 'reportsUbeyaBiTab',
                    to: '/:accountId/insights',
                    icon: SubmoduleIcons.ReportsBi
                  }
                ]
              : []),
            {
              label: 'reportsHistory',
              to: '/:accountId/reports-history',
              icon: SubmoduleIcons.PayrollHistory
            }
          ]
        },
        {
          label: 'clients',
          to: '/:accountId/crm',
          order: isHost ? 8 : 7,
          hasBottomBorder: isHost,
          disabled: isHost,
          icon: ModuleIcons.ModuleIconsLight.Clients,
          iconDark: ModuleIcons.ModuleIconsDark.CalendarDark,
          active: ModuleIcons.ModuleIconsActive.CalendarActive,
          isVisible: !!myPrivileges[privileges.CRM]
        },
        {
          label: 'agencies',
          to: '/:accountId/suppliers',
          order: isHost ? 2 : 8,
          hasBottomBorder: isHost,
          icon: ModuleIcons.ModuleIconsLight.Suppliers,
          iconDark: ModuleIcons.ModuleIconsDark.SuppliersDark,
          active: ModuleIcons.ModuleIconsActive.SuppliersActive,
          isVisible: !!myPrivileges[privileges.UBEYA_ADMIN] || !!myPrivileges[privileges.SUPPLIERS]
        },
        {
          label: 'aiInsightsAgent',
          to: '/:accountId/ai-insight-agent',
          order: 8.5,
          icon: ModuleIcons.ModuleIconsLight.Ai,
          iconDark: ModuleIcons.ModuleIconsDark.AiDark,
          active: ModuleIcons.ModuleIconsActive.AiActive,
          accessory: BetaAccessory,
          isVisible: canAccessAiAgent
        },
        {
          label: 'administration',
          to: '/:accountId/settings',
          order: 9,
          icon: ModuleIcons.ModuleIconsLight.Settings,
          iconDark: ModuleIcons.ModuleIconsDark.SettingsDark,
          active: ModuleIcons.ModuleIconsActive.SettingsActive,
          isVisible: !isAdminBlockedFromSettings({ email: adminEmail, isRestrictedFromEmployees }),
          secondaryMenu: [
            {
              label: 'accountSettings',
              to: '/:accountId/settings',
              icon: SubmoduleIcons.Settings,
              isHidden: ACCOUNT_SETTINGS_WITHOUT_EDIT_PERMISSIONS_ORGANIZATION_IDS.includes(account?.organizationId)
                ? false
                : !myPrivileges[privileges.EDIT_PERMISSIONS]
            },
            {
              label: 'integrations',
              to: '/:accountId/integrations',
              icon: SubmoduleIcons.AppStore,
              isHidden: isUbeyaAdmin
                ? false
                : !myPrivileges[privileges.EDIT_PERMISSIONS] || isAccountNetworkAccessSupplier || !modules.app_store
            },
            {
              label: 'myAdminSettings',
              icon: SubmoduleIcons.AdminSettings,
              to: '',
              onClick: () => setProfileModal(true)
            }
          ]
        }
      ].sort((a, b) => a.order - b.order)
    ],
    [
      LogoAccessory,
      isHost,
      myPrivileges,
      modules,
      isRestrictedFromEmployees,
      isAccountNetworkAccessSupplier,
      enablePayrollProcessFeature,
      account?.enableBi,
      account?.organizationId,
      isUbeyaAdmin,
      adminEmail,
      theme.colors.black,
      theme.colors.primary,
      canAccessAiAgent
    ]
  );

  return (
    <>
      <AppSideBar links={links} linkRenderer={AccountLink} mobileRenderer={<MobileTopBar />} />
      {profileModal && adminConfigData && (
        <ProfileModal
          update={updateAdminConfig}
          configInitialData={adminConfigData}
          onClose={() => setProfileModal(false)}
        />
      )}
    </>
  );
};

export default SideBar;
